import { Button } from "components/Button";
import { Modal } from "components/Modal";
import { useForm } from "react-hook-form";
import { projectsService } from "services/projectsService";
import { projectsStore } from "stores/projects";
import { ICryptoProject, TCryptoProjectForm } from "types/cryptoProject";
import { CryptoProjectCampaign } from "./CryptoProjectCampaign";
import { isValidUrl } from "utils/string";

type TProps = {
  projectToEdit: ICryptoProject | null;
};

export const ProjectEditForm = ({ projectToEdit }: TProps) => {
  const { isProjectOnEdit } = projectsStore.getState();

  const { watch, setValue } = useForm<TCryptoProjectForm>({
    defaultValues: {
      campaigns: projectToEdit?.campaigns || [],
      id: projectToEdit?.id || "",
    },
    mode: "all",
  });

  const campaigns = watch("campaigns");

  const isSaveDisabled =
    !campaigns.length
    || campaigns.some(campaign => !isValidUrl(campaign.link) || !campaign.banner);

  const addCampaign = () => {
    const newCampaign = {
      banner: "",
      link: "",
    };
    setValue("campaigns", [...campaigns, newCampaign]);
  };

  const deleteCampaign = (index: number) => {
    const updatedCampaigns = campaigns.filter((_, i) => i !== index);
    setValue("campaigns", updatedCampaigns);
  };

  const editCampaignBanner = (index: number, newBanner: string) => {
    const updatedCampaigns = campaigns.map((campaign, i) =>
      i === index ? { ...campaign, banner: newBanner } : campaign
    );
    setValue("campaigns", updatedCampaigns);
  };

  const editCampaignLink = (index: number, newLink: string) => {
    const updatedCampaigns = campaigns.map((campaign, i) =>
      i === index ? { ...campaign, link: newLink } : campaign
    );
    setValue("campaigns", updatedCampaigns);
  };

  const handleSave = async () => {
    if (!projectToEdit) return;

    try {
      await projectsService.updateProject({ id: projectToEdit?.id, campaigns });
      projectsService.closeEditProjectForm();
    } catch (error) {
      console.error("Error saving project:", error);
    }
  };

  return (
    <Modal
      isOpen={!!projectToEdit}
      onOpenChange={() => projectsService.closeEditProjectForm()}
    >
      <div className="p-8 w-[500px] flex flex-col">
        <div>
          <h2 className="text-2xl font-semibold">Project: {projectToEdit?.name}</h2>
          <p className="opacity-50">{projectToEdit?.twitter}</p>
        </div>

        <div className="mt-4">
          <p className="text-xl">Campaigns:</p>

          <ul className="flex flex-col gap-4">
            {campaigns.map((campaign, index) => (
              <CryptoProjectCampaign
                key={index}
                id={index.toString()}
                link={campaign.link}
                banner={campaign.banner}
                onDelete={() => deleteCampaign(index)}
                onEditBanner={(newBanner) => editCampaignBanner(index, newBanner)}
                onEditLink={(newLink) => editCampaignLink(index, newLink)}
              />
            ))}
          </ul>

          <Button onClick={addCampaign} extraClass="mt-2">
            + Add Campaign
          </Button>
        </div>

        <Button
          extraClass="mt-8 mx-auto"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {isProjectOnEdit ? "Saving..." : "Save"}
        </Button>
      </div>
    </Modal>
  );
};
